/**
 * Archivio «ultima lista» per codice accesso (PocketBase + backup localStorage).
 * Diverso da SessionPersistence: sopravvive tra sessioni/tab e alimenta «Carica ultima lista».
 */
import { log } from "../utils/logger.js";
import { savePlayerList, loadPlayerList } from "../services/db/lists.pocketbase.js";
import { sanitizePlayerListNames } from "../utils/decodeHtmlEntities.js";

export const persistenceMethods = {
  /**
   * Salva l'ultima lista giocatori per riutilizzo rapido
   */
  async savePlayersList(players) {
    try {
      const userSession = this.state.userSession;
      if (!userSession || !userSession.isAuthenticated()) {
        log.warn('Salvataggio lista: Utente non autenticato, skip');
        return { success: false, error: 'Utente non autenticato' };
      }

      const currentUser = userSession.getCurrentUser();
      const userCode = currentUser?.access_key || currentUser?.access_code;

      if (!userCode) {
        log.warn('Salvataggio lista: Codice utente non trovato');
        return { success: false, error: 'Codice utente non trovato' };
      }

      const playerListData = {
        players: players,
        count: players.length,
        userCode: userCode,
        timestamp: new Date().toISOString()
      };

      // Salva in localStorage come backup
      const storageKey = `fantapp_last_player_list_${userCode}`;
      localStorage.setItem(storageKey, JSON.stringify(playerListData));
      
      try {
        await savePlayerList({ accessKey: userCode, playerListData });
        log.info(`Lista salvata su PocketBase per ${currentUser.display_name}: ${players.length} giocatori`);
        return { success: true, source: 'pocketbase' };
      } catch (pbError) {
        log.warn('Salvataggio PocketBase fallito, mantengo localStorage:', pbError.message);
        return { success: true, source: 'localStorage', fallback: true };
      }
    } catch (error) {
      log.error('Errore salvataggio lista giocatori:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Carica l'ultima lista giocatori salvata
   */
  async loadLastPlayerList() {
    try {
      const userSession = this.state.userSession;
      if (!userSession || !userSession.isAuthenticated()) {
        return { success: false, error: 'Utente non autenticato' };
      }

      const currentUser = userSession.getCurrentUser();
      const userCode = currentUser?.access_key || currentUser?.access_code;
      
      console.log('[LOAD_PLAYERS] 👤 Current user:', {
        currentUser: currentUser,
        access_key: currentUser?.access_key,
        access_code: currentUser?.access_code,
        userCode: userCode
      });

      if (!userCode) {
        return { success: false, error: 'Codice utente non trovato' };
      }

      try {
        const row = await loadPlayerList(userCode);
        if (row) {
          const playerListData = row.player_list_data;
          if (playerListData?.players?.length > 0) {
            playerListData.players = sanitizePlayerListNames(playerListData.players);
            playerListData.count = playerListData.players.length;
            playerListData.timestamp = row.updated_at || row.created_at;
            const storageKey = `fantapp_last_player_list_${userCode}`;
            localStorage.setItem(storageKey, JSON.stringify(playerListData));
            log.info(`Lista caricata da PocketBase: ${playerListData.count} giocatori`);
            return { success: true, data: playerListData, source: 'pocketbase' };
          }
        }
      } catch (pocketbaseError) {
        log.warn('Errore PocketBase, fallback localStorage:', pocketbaseError.message);
      }

      // Fallback: carica da localStorage
      const storageKey = `fantapp_last_player_list_${userCode}`;
      const playerListJson = localStorage.getItem(storageKey);

      if (playerListJson) {
        const playerListData = JSON.parse(playerListJson);
        
        if (playerListData.players && 
            Array.isArray(playerListData.players) && 
            playerListData.players.length > 0 &&
            playerListData.userCode === userCode) {
          
          playerListData.players = sanitizePlayerListNames(playerListData.players);
          playerListData.count = playerListData.players.length;
          log.info(`Lista caricata da localStorage: ${playerListData.count} giocatori`);
          return { success: true, data: playerListData, source: 'localStorage' };
        }
      }

      return { success: false, error: 'Nessuna lista salvata per questo utente' };
    } catch (error) {
      log.error('Errore caricamento lista giocatori:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Ricarica l'ultima lista giocatori e aggiorna l'applicazione
   */
  async reloadLastPlayerList() {
    try {
      log.info('Ricaricamento ultima lista giocatori...');
      
      const result = await this.loadLastPlayerList();
      if (!result.success) {
        throw new Error(result.error);
      }
      
      const players = result.data.players;
      log.info(`Lista giocatori ricaricata: ${players.length} giocatori`);
      
      // Aggiorna i dati dell'applicazione
      await this.updatePlayersData(players);
      
      // Ricarica target Studio
      await this.reloadUserData();
      
      return {
        success: true,
        playerCount: players.length,
        withStats: players.filter(p => p.has_season_stats).length,
        message: 'Lista precedente ricaricata con successo'
      };
    } catch (error) {
      log.error('Errore ricaricamento lista:', error);
      return { success: false, error: error.message };
    }
  },
  
  /**
   * Aggiorna il pulsante di start
   */
  updateStartButton(playerCount, enabled = false) {
    const startButton = document.getElementById('start-auction-btn');
    if (startButton) {
      startButton.disabled = !enabled;
      startButton.textContent = enabled
        ? `Inizia asta (${playerCount})`
        : 'Inizia asta';
    }
  },
};
